/**
 * The delight layer: three weights of motion, shared by both apps.
 *
 * Deliberately not a dependency: this is a few dozen lines of canvas and Web Animations,
 * and the alternative is a package on every page that renders a notification.
 *
 * Two rules all three share:
 *
 *   - `prefers-reduced-motion` disables them entirely. This is not a nicety — a burst of
 *     moving particles is exactly what vestibular triggers are about. The state change
 *     still happens; only the motion does not.
 *   - Motion never gates the outcome. All three are fire-and-forget and cannot throw into
 *     the caller, so a dropped frame or a missing canvas can never change whether the
 *     thing was actually sent.
 *
 * THREE WEIGHTS, so that nothing feels dead and nothing feels cheap.
 *
 *   `celebrate` — a burst. For a gesture aimed at another person: a welcome, a greeting,
 *                 a congratulation, and for a milestone you reached yourself.
 *   `launch`    — one glyph leaving the control it came from. For a reaction, where the
 *                 emoji itself is the message being sent.
 *   `pop`       — a single beat of scale. For every other one-tap control, so a Follow or
 *                 a Connect answers the finger too. Cheap enough to be everywhere, which
 *                 is exactly why it is not the burst.
 *
 * Never on information — a batched row, a weekly summary, a moderation notice.
 *
 * Lives in `shared/` because the two apps had drifted: town-square's ReactionBar carried
 * `launch` and a stronger `pop` inline while the client's had neither, which is why the
 * quest modal felt better than the notifications modal for the same gesture.
 */

function reduced(): boolean {
  if (typeof window === 'undefined') return true;
  return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches);
}

const COLORS = ['#34d399', '#60a5fa', '#f472b6', '#fbbf24', '#a78bfa'];
const PARTICLE_COUNT = 44;
const DURATION_MS = 1100;
const GRAVITY = 0.16;

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  spin: number;
  angle: number;
}

/** Burst from the centre of `origin`, or the centre of the viewport when there is none. */
export function celebrate(origin?: Element | null): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (reduced()) return;

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return;

  const rect = origin?.getBoundingClientRect();
  const startX = rect ? rect.left + rect.width / 2 : window.innerWidth / 2;
  const startY = rect ? rect.top + rect.height / 2 : window.innerHeight / 2;

  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
  // Above the notification row and any open dialog, and never in the way of a click.
  canvas.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:2147483647';
  document.body.appendChild(canvas);

  const particles: Particle[] = Array.from({ length: PARTICLE_COUNT }, (_, index) => {
    // Fanned upward rather than in a full circle: a burst that also fires downward
    // reads as an explosion instead of a celebration.
    const angle = -Math.PI / 2 + (index / PARTICLE_COUNT - 0.5) * Math.PI * 1.2;
    const speed = 4 + Math.random() * 4;
    return {
      x: startX,
      y: startY,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      size: 4 + Math.random() * 4,
      // Provably in range: a modulo over a non-empty literal.
      color: COLORS[index % COLORS.length] as string,
      spin: (Math.random() - 0.5) * 0.3,
      angle: Math.random() * Math.PI,
    };
  });

  const started = performance.now();

  function frame(now: number) {
    const elapsed = now - started;
    if (elapsed >= DURATION_MS) {
      canvas.remove();
      return;
    }

    context!.clearRect(0, 0, canvas.width, canvas.height);
    context!.globalAlpha = Math.max(0, 1 - elapsed / DURATION_MS);

    for (const particle of particles) {
      particle.x += particle.vx;
      particle.y += particle.vy;
      particle.vy += GRAVITY;
      particle.angle += particle.spin;

      context!.save();
      context!.translate(particle.x, particle.y);
      context!.rotate(particle.angle);
      context!.fillStyle = particle.color;
      context!.fillRect(-particle.size / 2, -particle.size / 2, particle.size, particle.size * 0.6);
      context!.restore();
    }

    requestAnimationFrame(frame);
  }

  requestAnimationFrame(frame);
}

/**
 * A single beat of scale on the control that was tapped.
 *
 * It gives under the finger before springing back past its own size. A single overshoot
 * did not read as a press at all — that is the difference between a button you pressed
 * and a button that merely changed colour.
 *
 * Runs on the Web Animations API, so it cannot leave a transform behind if the element
 * unmounts mid-animation the way a CSS class toggle can.
 */
export function pop(element?: Element | null): void {
  if (!element || reduced()) return;
  if (typeof (element as HTMLElement).animate !== 'function') return;

  (element as HTMLElement).animate(
    [
      { transform: 'scale(1)', offset: 0 },
      { transform: 'scale(0.88)', offset: 0.18 },
      { transform: 'scale(1.18)', offset: 0.55 },
      { transform: 'scale(1)', offset: 1 },
    ],
    // 300ms: long enough to read as a response, short enough that a second tap is not
    // waiting on the first.
    { duration: 300, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }
  );
}

/**
 * The glyph leaves the control it came from and drifts up out of the card.
 *
 * For the way IN only. Taking a reaction back is a correction, and celebrating a
 * correction is the thing that makes an interface feel like it is not listening.
 *
 * Fixed-position on `document.body` rather than inside the control: cards and pickers
 * clip their overflow, so a glyph parented to the button would be sliced off at the
 * border.
 */
export function launch(element: Element | null, glyph: string): void {
  if (!element || reduced() || typeof document === 'undefined') return;

  const box = element.getBoundingClientRect();
  const flyer = document.createElement('span');
  flyer.textContent = glyph;
  flyer.setAttribute('aria-hidden', 'true');
  Object.assign(flyer.style, {
    position: 'fixed',
    left: `${box.left + box.width / 2}px`,
    top: `${box.top + box.height / 2}px`,
    fontSize: '22px',
    lineHeight: '1',
    pointerEvents: 'none',
    zIndex: '2147483647',
    transform: 'translate(-50%, -50%)',
    transition: 'transform 700ms cubic-bezier(0.2, 0.8, 0.3, 1), opacity 700ms ease-out',
  });
  document.body.appendChild(flyer);

  // A little sideways scatter so stacking three of them does not look like one glyph
  // blinking three times.
  const drift = (Math.random() - 0.5) * 40;
  requestAnimationFrame(() => {
    flyer.style.transform = `translate(calc(-50% + ${drift}px), -180%) scale(1.9)`;
    flyer.style.opacity = '0';
  });
  window.setTimeout(() => flyer.remove(), 750);
}
